import React from 'react';
import _ from 'lodash';
import LivePage from './LivePage';
import submenuTests from './menus/submenu-tests';
import { ImagePreview } from '../components/images/ImagePreview';
import BadgeColorHash from '../components/common/BadgeColorHash';
import SimilarityBadge from '../components/common/SimilarityBadge';
import ClusterPairExplorer from '../components/embedding-explorer/ClusterPairExplorer';
import { prefetchClusterItems } from '../components/embedding-explorer/clusterItemsLoader';
import { IconButton } from '../components/common/IconButton';

const PHOTO_TYPES = ['open_fusebox', 'plastic_cover', 'other_cluster', 'manual_page', 'empty_template', 'exclude'];

const GROUP_COLORS = ['#2563eb', '#dc2626'];

function downloadJsonl(filename, rows) {
  const blob = new Blob([`${rows.map((r) => JSON.stringify(r)).join('\n')}\n`], { type: 'application/jsonl' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}


/** /processed/.../{s|m|l}/file → /originals/.../file (inverse of getImageThumbnail) */
function toOriginalImageUrl(imgUrl) {
  imgUrl = imgUrl || '';
  if (!imgUrl || imgUrl.includes('/originals/')) {
    return imgUrl;
  }
  return imgUrl.replace(
    /\/processed\/((?:[^/]+\/)+)([sml])\/([^/]+)$/i,
    '/originals/$1$3',
  );
}

function imagePreviewUrl(row) {
  return toOriginalImageUrl(row?.image_url_original || row?.image_url || '');
}

function buildPhotoReviewClusters(mixedClusters, photoSuggestions, clusters) {
  const suggestionsByCluster = _.groupBy(photoSuggestions, 'cluster_id');
  const clusterByIdMap = _.keyBy(clusters, 'cluster_id');

  return mixedClusters
    .map((summary) => {
      const cluster = clusterByIdMap[summary.cluster_id];
      const suggestionByImageId = _.keyBy(suggestionsByCluster[summary.cluster_id] || [], 'image_id');
      const images = (cluster?.images || []).map((img) => {
        const suggestion = suggestionByImageId[img.image_id] || {};
        return {
          image_id: img.image_id,
          image_url: img.image_url || suggestion.image_url || '',
          image_url_original: img.image_url_original || suggestion.image_url_original || '',
          suggested_group: suggestion.suggested_group,
          separation_score: suggestion.separation_score || summary.separation_score,
        };
      }).filter((img) => imagePreviewUrl(img) && img.suggested_group != null && img.suggested_group !== '');

      const group0 = images.filter((img) => String(img.suggested_group) === '0');
      const group1 = images.filter((img) => String(img.suggested_group) === '1');

      return {
        summary,
        cluster,
        group0,
        group1,
      };
    })
    .filter((item) => item.group0.length > 0 && item.group1.length > 0);
}

async function loadReviewFile(reviewSvc, name) {
  try {
    return await reviewSvc.get(encodeURIComponent(name));
  } catch (err) {
    console.warn(`Failed to load ${name}`, err);
    return { rows: [], error: String(err.message || err) };
  }
}

function twinPairKey(clusterIdA, clusterIdB) {
  return [String(clusterIdA), String(clusterIdB)].sort().join('|');
}

/** Small button linking to the image-clusters admin filtered to the pair's two clusters. */
function ClusterPairAdminLink({ clusterIdA, clusterIdB }) {
  const filters = JSON.stringify({ _id: { $in: [String(clusterIdA), String(clusterIdB)] } });
  return <a className="btn btn-sm btn-outline-secondary ml-2 py-0" target="_blank" rel="noreferrer"
    href={`/legos/image-clusters?filters=${encodeURIComponent(filters)}`}
    title="Open both clusters in the image-clusters admin">⧉ clusters</a>;
}

function overrideDedupeKey(row) {
  if (!row || !row.type) {
    return '';
  }
  switch (row.type) {
    case 'photo_type':
      return row.image_id ? `photo_type:${String(row.image_id)}` : '';
    case 'reject_twin':
    case 'reject_link':
    case 'confirm_link':
    case 'related_clusters':
      return row.cluster_id_a && row.cluster_id_b
        ? `${row.type}:${twinPairKey(row.cluster_id_a, row.cluster_id_b)}`
        : '';
    case 'link_clusters':
    case 'global_template_link':
      return row.cluster_ids?.length >= 2
        ? `${row.type}:${twinPairKey(row.cluster_ids[0], row.cluster_ids[1])}`
        : '';
    case 'merge_clusters':
      return row.drop_cluster_id ? `merge_clusters:${String(row.drop_cluster_id)}` : '';
    default:
      return '';
  }
}

function overridePayload(row) {
  if (!row || !row.type) {
    return null;
  }
  switch (row.type) {
    case 'photo_type':
      return {
        type: row.type,
        image_id: String(row.image_id),
        photo_type: row.photo_type,
        cluster_id: row.cluster_id ? String(row.cluster_id) : '',
      };
    case 'reject_twin':
    case 'reject_link':
    case 'confirm_link':
    case 'related_clusters': {
      // The pair is unordered — canonicalize so {a,b} and {b,a} compare equal.
      const [a, b] = [String(row.cluster_id_a), String(row.cluster_id_b)].sort();
      return { type: row.type, cluster_id_a: a, cluster_id_b: b };
    }
    case 'link_clusters':
    case 'global_template_link':
      return {
        type: row.type,
        cluster_ids: [...(row.cluster_ids || [])].map(String).sort(),
        global_template_group_id: String(row.global_template_group_id || ''),
      };
    case 'merge_clusters':
      return {
        type: row.type,
        keep_cluster_id: String(row.keep_cluster_id),
        drop_cluster_id: String(row.drop_cluster_id),
      };
    default:
      return null;
  }
}

function overridesEqual(a, b) {
  const pa = overridePayload(a);
  const pb = overridePayload(b);
  if (!pa || !pb) {
    return false;
  }
  return JSON.stringify(pa) === JSON.stringify(pb);
}

function filterNewOverrides(existingRows, incomingRows) {
  const index = new Map();
  for (const row of existingRows || []) {
    const key = overrideDedupeKey(row);
    if (key) {
      index.set(key, row);
    }
  }

  const toWrite = [];
  let skipped = 0;

  for (const row of incomingRows || []) {
    const key = overrideDedupeKey(row);
    if (!key) {
      toWrite.push(row);
      continue;
    }
    const prev = index.get(key);
    if (prev && overridesEqual(prev, row)) {
      skipped++;
      continue;
    }
    toWrite.push(row);
    index.set(key, row);
  }

  return { toWrite, skipped };
}

function parsePhotoTypeOverrides(rows) {
  const photoTypes = new Map();
  for (const row of rows || []) {
    if (row.type === 'photo_type' && row.image_id) {
      photoTypes.set(String(row.image_id), row.photo_type);
    }
  }
  return photoTypes;
}

/**
 * The full "same template" edge graph over every decision so far (saved + pending). Mirrors
 * lib/linkGraph.js semantics: edges = human links + merges + confirms, MINUS rejected pairs
 * (fusebox-template relations are ignored — too noisy); components are transitive, and
 * rejections lift to component level (A≈A' and A'≠B ⇒ A≠B). This is what makes one decision
 * prune every implied pair from the queues live, without rebuilding moderation.json.
 */
function buildModerationGraph(rows) {
  const edgeKeys = new Set();
  const rejectedKeys = new Set();
  const confirmedKeys = new Set();
  // related = resolved-but-neutral: drops the pair from the queues without linking or rejecting it.
  const relatedKeys = new Set();
  const addEdge = (a, b) => {
    if (a && b && String(a) !== String(b)) {
      edgeKeys.add(twinPairKey(a, b));
    }
  };

  for (const row of rows || []) {
    if ((row.type === 'global_template_link' || row.type === 'link_clusters') && row.cluster_ids?.length >= 2) {
      const ids = row.cluster_ids.map(String);
      for (let i = 0; i < ids.length; i += 1) {
        for (let j = i + 1; j < ids.length; j += 1) {
          addEdge(ids[i], ids[j]);
        }
      }
    } else if (row.type === 'merge_clusters' && row.keep_cluster_id && row.drop_cluster_id) {
      addEdge(row.keep_cluster_id, row.drop_cluster_id);
    } else if (row.type === 'confirm_link' && row.cluster_id_a && row.cluster_id_b) {
      confirmedKeys.add(twinPairKey(row.cluster_id_a, row.cluster_id_b));
      addEdge(row.cluster_id_a, row.cluster_id_b);
    } else if ((row.type === 'reject_twin' || row.type === 'reject_link') && row.cluster_id_a && row.cluster_id_b) {
      rejectedKeys.add(twinPairKey(row.cluster_id_a, row.cluster_id_b));
    } else if (row.type === 'related_clusters' && row.cluster_id_a && row.cluster_id_b) {
      relatedKeys.add(twinPairKey(row.cluster_id_a, row.cluster_id_b));
    }
  }
  const parent = new Map();
  const find = (x) => {
    if (!parent.has(x)) {
      parent.set(x, x);
      return x;
    }
    while (parent.get(x) !== x) {
      parent.set(x, parent.get(parent.get(x))); // path-halving
      x = parent.get(x);
    }
    return x;
  };
  for (const key of edgeKeys) {
    if (rejectedKeys.has(key)) {
      continue; // a rejected edge is removed — this is how breaking a link splits a group
    }
    const [a, b] = key.split('|');
    parent.set(find(a), find(b));
  }
  const rootOf = (x) => (parent.has(String(x)) ? find(String(x)) : String(x));
  const connected = (a, b) => String(a) !== String(b) && rootOf(a) === rootOf(b);

  // Rejections between two distinct components reject the whole group pair. Rejections whose
  // endpoints are still connected are contradictions — they must stay visible, not filter.
  const rejectedRootKeys = new Set();
  for (const key of rejectedKeys) {
    const [a, b] = key.split('|');
    const ra = rootOf(a);
    const rb = rootOf(b);
    if (ra !== rb) {
      rejectedRootKeys.add(ra < rb ? `${ra}|${rb}` : `${rb}|${ra}`);
    }
  }
  const rejectedBetween = (a, b) => {
    const ra = rootOf(a);
    const rb = rootOf(b);
    if (ra === rb) {
      return false;
    }
    return rejectedRootKeys.has(ra < rb ? `${ra}|${rb}` : `${rb}|${ra}`);
  };

  // 'related' is transitive among related fuseboxes: A~B and B~C ⇒ A~C. Build a separate union-find
  // over related edges, joining each side's LINK-group root so same-template clusters share one
  // related node (A linked B, B~C ⇒ A~C). A pair is resolved-as-related when both clusters fall in
  // the same related component. (Related still neither links nor rejects — it's its own grouping.)
  const relatedParent = new Map();
  const relatedFind = (x) => {
    if (!relatedParent.has(x)) {
      relatedParent.set(x, x);
      return x;
    }
    while (relatedParent.get(x) !== x) {
      relatedParent.set(x, relatedParent.get(relatedParent.get(x))); // path-halving
      x = relatedParent.get(x);
    }
    return x;
  };
  for (const key of relatedKeys) {
    const [a, b] = key.split('|');
    relatedParent.set(relatedFind(rootOf(a)), relatedFind(rootOf(b)));
  }
  const relatedBetween = (a, b) => {
    if (String(a) === String(b)) {
      return false;
    }
    const ra = rootOf(a);
    const rb = rootOf(b);
    if (!relatedParent.has(ra) || !relatedParent.has(rb)) {
      return false;
    }
    return relatedFind(ra) === relatedFind(rb);
  };

  return { connected, rejectedBetween, relatedBetween, rejectedKeys, confirmedKeys, relatedKeys };
}

function isPhotoClusterFullyLabeled(photoCluster, photoTypes) {
  const images = [...photoCluster.group0, ...photoCluster.group1];
  if (!images.length) {
    return false;
  }
  return images.every((img) => photoTypes.has(String(img.image_id)));
}

function filterPhotoClusters(photoClusters, photoTypes) {
  return photoClusters.filter((pc) => !isPhotoClusterFullyLabeled(pc, photoTypes));
}

function buildGroupLabelsFromOverrides(photoTypes) {
  // Labels are stored per-image; the group panel derives its shared/mixed state from these.
  const groupLabels = {};
  for (const [imageId, photoType] of photoTypes) {
    groupLabels[`image:${imageId}`] = photoType;
  }
  return groupLabels;
}

export default class FuseboxDatasetReviewPage extends LivePage {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      tab: 'twins',
      loading: false,
      error: null,
      sampleName: '',
      samples: [],
      generatedAt: '',
      thresholds: {},
      stats: {},
      clusters: [],
      // Raw queues exactly as moderation.json shipped them; the visible*() getters filter them
      // live against every decision (saved + pending), so one decision prunes all implied pairs.
      twinPairsAll: [],
      photoSuggestions: [],
      mixedClusters: [],
      photoClusters: [],
      crossModelPairsAll: [],
      linkedButFarAll: [],
      contradictions: [],
      twinIndex: 0,
      photoIndex: 0,
      crossIndex: 0,
      linkedIndex: 0,
      pendingOverrides: [],
      loadedOverrideRows: [],
      loadedOverrideCount: 0,
      groupLabels: {},
      loadWarnings: [],
      // Search-and-jump modal: which queue's index it targets ('twinIndex'|'crossIndex'|'linkedIndex'), and the regex.
      searchOpen: null,
      searchQuery: '',
      searchShowAll: false,
    };
    this.submenu = submenuTests;
    this.reviewSvc = this.service('services/fusebox-dataset-review');
  }

  componentDidMount() {
    super.componentDidMount();
    this.loadData();
  }

  componentDidUpdate(prevProps, prevState) {
    // Whenever the visible pair of a pair queue changes (tab switch, index moved, a decision
    // shrank the queue, or new data loaded), warm the embeddings cache for the next pairs.
    const s = this.state;
    const watched = ['tab', 'twinIndex', 'crossIndex', 'linkedIndex',
      'twinPairsAll', 'crossModelPairsAll', 'linkedButFarAll', 'pendingOverrides', 'loadedOverrideRows'];
    if (['twins', 'cross', 'linked'].includes(s.tab) && watched.some((key) => prevState[key] !== s[key])) {
      this.prefetchUpcomingPairEmbeddings();
    }
  }

  /** Pre-loads both clusters' embeddings for the current + next 2 pairs of the active queue. */
  prefetchUpcomingPairEmbeddings() {
    const queueOf = {
      twins: () => [this.visibleTwinPairs(), this.state.twinIndex],
      cross: () => [this.visibleCrossModelPairs(), this.state.crossIndex],
      linked: () => [this.visibleLinkedButFar(), this.state.linkedIndex],
    }[this.state.tab];
    if (!queueOf) {
      return;
    }
    const [pairs, index] = queueOf();
    const current = Math.min(index, pairs.length - 1);
    for (let n = 0; n <= 4; n += 1) {
      const pair = pairs[current + n];
      if (!pair) {
        break;
      }
      prefetchClusterItems(this, pair.cluster_id_a);
      prefetchClusterItems(this, pair.cluster_id_b);
    }
  }

  async loadData(sampleName = this.state.sampleName) {
    const { pendingOverrides } = this.state;
    this.setState({ loading: true, error: null });
    try {
      // The payload is precomputed by buildModeration.js; the service just serves the file.
      const result = await this.reviewSvc.find({ query: sampleName ? { name: sampleName } : {} });
      if (result.error) {
        this.setState({ loading: false, error: result.error, samples: result.samples || [] });
        return;
      }
      // Merged override rows (legacy baseline + this sample's file).
      const overridesRes = await loadReviewFile(this.reviewSvc, result.name);

      const clusters = result.clusters || [];
      const mixedClusters = result.mixedClusters || [];
      const photoSuggestions = result.photoSuggestions || [];
      const overrideRows = overridesRes.rows || [];
      const photoTypes = parsePhotoTypeOverrides(overrideRows);
      const loadWarnings = [
        overridesRes.error && !/not found/i.test(overridesRes.error) && `dataset_overrides.jsonl: ${overridesRes.error}`,
      ].filter(Boolean);

      const allPhotoClusters = buildPhotoReviewClusters(mixedClusters, photoSuggestions, clusters);
      const photoClusters = filterPhotoClusters(allPhotoClusters, photoTypes);
      const groupLabels = buildGroupLabelsFromOverrides(photoTypes);

      this.setState({
        loading: false,
        sampleName: result.name,
        samples: result.samples || [],
        generatedAt: result.generated_at || '',
        thresholds: result.thresholds || {},
        stats: result.stats || {},
        clusters,
        twinPairsAll: result.twinPairs || [],
        photoSuggestions,
        mixedClusters,
        photoClusters,
        crossModelPairsAll: result.crossModelPairs || [],
        linkedButFarAll: result.linkedButFar || [],
        contradictions: result.contradictions || [],
        twinIndex: 0,
        photoIndex: 0,
        crossIndex: 0,
        linkedIndex: 0,
        groupLabels,
        loadedOverrideRows: overrideRows,
        loadedOverrideCount: overrideRows.length,
        pendingOverrides,
        loadWarnings,
      });
    } catch (err) {
      this.setState({ loading: false, error: String(err.message || err) });
    }
  }

  queueOverride(entry) {
    const key = overrideDedupeKey(entry);
    // Functional update so callers can queue many overrides in one synchronous loop (e.g. a whole
    // photo group) without each call reading stale this.state and clobbering the previous ones.
    this.setState((prev) => {
      if (!key) {
        return { pendingOverrides: [...prev.pendingOverrides, entry] };
      }

      const saved = (prev.loadedOverrideRows || []).find((row) => overrideDedupeKey(row) === key);
      if (saved && overridesEqual(saved, entry)) {
        return null;
      }

      const pending = [...prev.pendingOverrides];
      const idx = pending.findIndex((row) => overrideDedupeKey(row) === key);
      if (idx >= 0) {
        if (overridesEqual(pending[idx], entry)) {
          return null;
        }
        pending[idx] = entry;
      } else {
        pending.push(entry);
      }
      return { pendingOverrides: pending };
    });
  }

  /** Removes any pending (unsaved) override sharing the dedupe key of `entry`. */
  unqueueOverride(entry) {
    const key = overrideDedupeKey(entry);
    if (!key) {
      return;
    }
    this.setState((prev) => ({
      pendingOverrides: prev.pendingOverrides.filter((row) => overrideDedupeKey(row) !== key),
    }));
  }

  async flushOverrides() {
    const { pendingOverrides, loadedOverrideRows } = this.state;
    if (!pendingOverrides.length) {
      return;
    }

    const { toWrite, skipped } = filterNewOverrides(loadedOverrideRows, pendingOverrides);
    if (!toWrite.length) {
      this.setState({ pendingOverrides: [] });
      this.showNotification({
        type: 'info',
        message: skipped
          ? `Nothing new to save (${skipped} duplicate override(s) already on file).`
          : 'Nothing new to save.',
      });
      return;
    }

    const res = await this.reviewSvc.create({ name: this.state.sampleName, overrides: toWrite });
    const serverSkipped = res.skipped || 0;
    const totalSkipped = skipped + serverSkipped;
    this.setState({ pendingOverrides: [] });
    const msg = totalSkipped
      ? `Saved ${res.written} override(s) (${totalSkipped} duplicate(s) skipped)`
      : `Saved ${res.written} override(s) to image_cluster_links + ${this.state.sampleName}/dataset_overrides.jsonl`;
    this.showNotification({ type: 'success', message: msg });
    await this.loadData();
  }

  downloadPendingOverrides() {
    const { pendingOverrides } = this.state;
    if (!pendingOverrides.length) {
      return;
    }
    downloadJsonl(`${this.state.sampleName || 'sample'}_dataset_overrides.jsonl`, pendingOverrides);
  }

  handleTwinLink = () => {
    const pair = this.currentOf(this.visibleTwinPairs(), this.state.twinIndex);
    if (!pair) {
      return;
    }
    const globalId = `linked:${pair.model_id}:${[pair.cluster_id_a, pair.cluster_id_b].sort().join(':')}`;
    this.queueOverride({
      type: 'link_clusters',
      cluster_ids: [pair.cluster_id_a, pair.cluster_id_b],
      global_template_group_id: globalId,
    });
    // No index advance: the decided pair drops out of the visible queue (see visibleCandidatePairs).
  };

  handleTwinReject = () => {
    const pair = this.currentOf(this.visibleTwinPairs(), this.state.twinIndex);
    if (!pair) {
      return;
    }
    this.queueOverride({
      type: 'reject_twin',
      cluster_id_a: pair.cluster_id_a,
      cluster_id_b: pair.cluster_id_b,
    });
  };

  /** Marks the current pair of a visible queue as 'related' (neutral: resolves without link/reject). */
  markRelated(visiblePairs, index) {
    const pair = this.currentOf(visiblePairs, index);
    if (!pair) {
      return;
    }
    this.queueOverride({
      type: 'related_clusters',
      cluster_id_a: pair.cluster_id_a,
      cluster_id_b: pair.cluster_id_b,
    });
  }

  handleTwinRelated = () => this.markRelated(this.visibleTwinPairs(), this.state.twinIndex);
  handleCrossModelRelated = () => this.markRelated(this.visibleCrossModelPairs(), this.state.crossIndex);
  handleLinkedRelated = () => this.markRelated(this.visibleLinkedButFar(), this.state.linkedIndex);

  handlePhotoGroupLabel = (group, photoType) => {
    const photoCluster = this.state.photoClusters[this.state.photoIndex];
    if (!photoCluster) {
      return;
    }
    const clusterId = photoCluster.summary.cluster_id;
    const images = group === 0 ? photoCluster.group0 : photoCluster.group1;
    // The group buttons are just a convenience: they stamp the same *individual* label on every
    // image in the group, so individual clicks can then override specific images. Re-clicking the
    // label that every image already has toggles it off for the whole group.
    const removing = images.length > 0
      && images.every((row) => this.state.groupLabels[`image:${row.image_id}`] === photoType);

    for (const row of images) {
      if (removing) {
        this.unqueueOverride({ type: 'photo_type', image_id: row.image_id });
      } else {
        this.queueOverride({ type: 'photo_type', image_id: row.image_id, photo_type: photoType, cluster_id: clusterId });
      }
    }
    this.setState((prev) => {
      const groupLabels = { ...prev.groupLabels };
      for (const row of images) {
        if (removing) delete groupLabels[`image:${row.image_id}`];
        else groupLabels[`image:${row.image_id}`] = photoType;
      }
      return { groupLabels };
    });
  };

  handlePhotoImageOverride = (imageId, photoType, clusterId) => {
    // Re-clicking the image's active label toggles it off.
    const removing = this.state.groupLabels[`image:${imageId}`] === photoType;
    if (removing) {
      this.unqueueOverride({ type: 'photo_type', image_id: imageId });
    } else {
      this.queueOverride({ type: 'photo_type', image_id: imageId, photo_type: photoType, cluster_id: clusterId });
    }
    this.setState((prev) => {
      const groupLabels = { ...prev.groupLabels };
      if (removing) {
        delete groupLabels[`image:${imageId}`];
      } else {
        groupLabels[`image:${imageId}`] = photoType;
      }
      return { groupLabels };
    });
  };

  // Labels are now stored purely per-image (`image:<id>`); the group panel derives its state from
  // the images it contains, so a group click and individual clicks operate on the same data.
  getPhotoTypeLabel = (clusterId, group, imageId) => (imageId ? this.state.groupLabels[`image:${imageId}`] : '') || '';

  /** The label shared by every image in a group, or '' when the group is unlabeled or mixed. */
  groupSharedLabel = (images) => {
    if (!images.length) {
      return '';
    }
    const first = this.state.groupLabels[`image:${images[0].image_id}`] || '';
    if (!first) {
      return '';
    }
    return images.every((img) => this.state.groupLabels[`image:${img.image_id}`] === first) ? first : '';
  };

  renderPhotoGroupPanel(group, groupImages, clusterId, title) {
    const sharedLabel = this.groupSharedLabel(groupImages);
    const mixed = !sharedLabel && groupImages.some((img) => this.state.groupLabels[`image:${img.image_id}`]);

    return <div className="col-md-6">
      <h6 style={{ color: GROUP_COLORS[group] }}>
        {title} ({groupImages.length})
        {sharedLabel ? <span className="badge badge-light ml-2">{sharedLabel}</span> : null}
        {mixed ? <span className="badge badge-warning ml-2">mixed</span> : null}
      </h6>
      <div className="mb-2">
        {PHOTO_TYPES.map((pt) => <button key={pt} type="button" className={`btn btn-sm mr-1 mb-1 ${sharedLabel === pt ? 'btn-success' : 'btn-outline-secondary'}`} onClick={() => this.handlePhotoGroupLabel(group, pt)}>{pt}</button>)}
      </div>
      <div className="embedding-grid bg-dark p-2">
        {groupImages.map((row) => {
          const imageLabel = this.getPhotoTypeLabel(clusterId, group, row.image_id);
          return <div key={row.image_id} className="embedding-thumb" style={{ border: `3px solid ${GROUP_COLORS[group]}` }}>
            <ImagePreview url={row.image_url} img200 contain zoom title={imagePreviewUrl(row)} />
            {imageLabel && !PHOTO_TYPES.includes(imageLabel) ? <div className="small text-center text-success">{imageLabel}</div> : null}
            <div className="btn-group-vertical btn-group-sm w-100 mt-1">
              {PHOTO_TYPES.map((pt) => <button key={pt} type="button" className={`btn btn-sm py-0 ${imageLabel === pt ? 'btn-success' : 'btn-dark'}`} onClick={() => this.handlePhotoImageOverride(row.image_id, pt, clusterId)}>{pt}</button>)}
            </div>
          </div>;
        })}
      </div>
    </div>;
  }

  /**
   * Edge graph over every decision so far (saved + pending this session). Memoized by reference
   * identity of its inputs, so it rebuilds only when an override is queued/saved or a new
   * sample loads.
   */
  moderationGraph() {
    const { loadedOverrideRows, pendingOverrides } = this.state;
    const c = this._graphCache;
    if (c && c.loaded === loadedOverrideRows && c.pending === pendingOverrides) {
      return c.graph;
    }
    const graph = buildModerationGraph([...(loadedOverrideRows || []), ...pendingOverrides]);
    this._graphCache = { loaded: loadedOverrideRows, pending: pendingOverrides, graph };
    return graph;
  }

  // A candidate pair still needs asking unless its clusters are already same-group (directly or
  // transitively), a rejection between their two groups already answers it, or it was marked
  // 'related' (resolved-but-neutral). Because this runs against pending overrides too, one
  // decision immediately prunes every implied pair.
  visibleCandidatePairs(pairs) {
    const graph = this.moderationGraph();
    return pairs.filter((p) => !graph.connected(p.cluster_id_a, p.cluster_id_b)
      && !graph.rejectedBetween(p.cluster_id_a, p.cluster_id_b)
      && !graph.relatedBetween(p.cluster_id_a, p.cluster_id_b));
  }

  visibleTwinPairs() {
    return this.visibleCandidatePairs(this.state.twinPairsAll);
  }

  visibleCrossModelPairs() {
    return this.visibleCandidatePairs(this.state.crossModelPairsAll);
  }

  visibleLinkedButFar() {
    const graph = this.moderationGraph();
    return this.state.linkedButFarAll.filter((p) => {
      const key = twinPairKey(p.cluster_id_a, p.cluster_id_b);
      return !graph.confirmedKeys.has(key) && !graph.rejectedKeys.has(key) && !graph.relatedKeys.has(key);
    });
  }

  /** Current item of a dynamically-shrinking queue: clamp so removals never strand the index. */
  currentOf(pairs, index) {
    return pairs[Math.min(index, pairs.length - 1)];
  }

  handleCrossModelLink = () => {
    const pair = this.currentOf(this.visibleCrossModelPairs(), this.state.crossIndex);
    if (!pair) {
      return;
    }
    const globalId = `linked:cross:${[pair.cluster_id_a, pair.cluster_id_b].sort().join(':')}`;
    this.queueOverride({
      type: 'global_template_link',
      cluster_ids: [pair.cluster_id_a, pair.cluster_id_b],
      global_template_group_id: globalId,
    });
    // No index advance: the decided pair (and every pair it answers by transitivity) drops out
    // of the visible queue, so the same index now shows the next open question.
  };

  handleCrossModelReject = () => {
    const pair = this.currentOf(this.visibleCrossModelPairs(), this.state.crossIndex);
    if (!pair) {
      return;
    }
    // Same confirmed-different relation as the twin queue; group-lifting prunes every other
    // pair between these two clusters' groups.
    this.queueOverride({
      type: 'reject_twin',
      cluster_id_a: pair.cluster_id_a,
      cluster_id_b: pair.cluster_id_b,
    });
  };

  handleLinkedConfirm = () => {
    const pair = this.currentOf(this.visibleLinkedButFar(), this.state.linkedIndex);
    if (!pair) {
      return;
    }
    this.queueOverride({
      type: 'confirm_link',
      cluster_id_a: pair.cluster_id_a,
      cluster_id_b: pair.cluster_id_b,
    });
  };

  handleLinkedReject = () => {
    const pair = this.currentOf(this.visibleLinkedButFar(), this.state.linkedIndex);
    if (!pair) {
      return;
    }
    this.queueOverride({
      type: 'reject_link',
      cluster_id_a: pair.cluster_id_a,
      cluster_id_b: pair.cluster_id_b,
    });
  };

  /** Small "jump to #N" control (1-based, matching the X/Y counter) for a given tab's index. */
  renderJump(indexKey, total) {
    const inputKey = `${indexKey}JumpInput`;
    const value = this.state[inputKey] == null ? '' : this.state[inputKey];
    const jump = () => {
      const n = parseInt(value, 10);
      if (!Number.isFinite(n) || !total) {
        return;
      }
      this.setState({ [indexKey]: Math.max(1, Math.min(total, n)) - 1 });
    };
    return <span className="ml-2 no-wrap">
      <input
        type="number"
        min="1"
        max={total}
        value={value}
        placeholder="#"
        style={{ width: '70px' }}
        className="form-control form-control-sm d-inline-block mr-1 align-middle"
        onChange={(e) => this.setState({ [inputKey]: e.target.value })}
        onKeyDown={(e) => { if (e.key === 'Enter') jump(); }}
      />
      <button type="button" className="btn btn-sm btn-outline-secondary align-middle" onClick={jump}>Jump</button>
    </span>;
  }

  /** The live (visible) queue a given index key navigates, for the search modal. */
  pairsForIndexKey(indexKey) {
    return {
      twinIndex: this.visibleTwinPairs(),
      crossIndex: this.visibleCrossModelPairs(),
      linkedIndex: this.visibleLinkedButFar(),
    }[indexKey] || [];
  }

  /** "Search" button (sits next to Jump) opening the regex search-and-jump modal for a queue. */
  renderSearch(indexKey) {
    return <IconButton icon="search"
      title="Search this queue by clusterId or modelId (regex) and jump to a match"
      onClick={() => this.setState({ searchOpen: indexKey, searchQuery: '', searchShowAll: false })}
    />;
  }

  /** Modal: regex search over the active queue by clusterId/modelId, live-filtered, click a row to jump. */
  renderSearchModal() {
    const indexKey = this.state.searchOpen;
    if (!indexKey) {
      return null;
    }
    const pairs = this.pairsForIndexKey(indexKey);
    const query = this.state.searchQuery || '';
    let regex = null;
    let regexError = null;
    if (query) {
      try {
        regex = new RegExp(query, 'i');
      } catch (err) {
        regexError = String(err.message || err);
      }
    }
    const rows = pairs.map((p, i) => ({ p, i })).filter(({ p }) => {
      if (!regex) {
        return true;
      }
      return [p.cluster_id_a, p.cluster_id_b, p.model_id, p.model_id_a, p.model_id_b]
        .filter(Boolean).map(String).some((f) => regex.test(f));
    });
    // Cap the rendered rows so a huge queue doesn't make React sluggish; "Show all" lifts the cap.
    const LIMIT = 200;
    const capped = !this.state.searchShowAll && rows.length > LIMIT;
    const visibleRows = capped ? rows.slice(0, LIMIT) : rows;
    const close = () => this.setState({ searchOpen: null });

    return <div
      onClick={close}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '60px' }}
    >
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded shadow" style={{ width: '90%', maxWidth: '720px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div className="p-3 border-bottom d-flex align-items-center">
          <input
            autoFocus
            type="text"
            className="form-control form-control-sm"
            placeholder="Search by clusterId or modelId (regex)…"
            value={query}
            onChange={(e) => this.setState({ searchQuery: e.target.value, searchShowAll: false })}
            onKeyDown={(e) => { if (e.key === 'Escape') close(); }}
          />
          <button type="button" className="btn btn-sm btn-outline-secondary ml-2" onClick={close}>Close</button>
        </div>
        {regexError ? <div className="text-danger small px-3 pt-2">Invalid regex: {regexError}</div> : null}
        <div className="small text-muted px-3 pt-2 d-flex align-items-center">
          <span>{rows.length} / {pairs.length} match(es){capped ? ` — showing first ${LIMIT}` : ''}</span>
          {capped ? <button type="button" className="btn btn-sm btn-link py-0 ml-2" onClick={() => this.setState({ searchShowAll: true })}>Show all</button> : null}
        </div>
        <div style={{ overflowY: 'auto' }} className="p-2">
          <table className="table table-sm table-hover mb-0">
            <thead><tr><th>#</th><th>Model</th><th>Cluster A</th><th>Cluster B</th></tr></thead>
            <tbody>
              {visibleRows.map(({ p, i }) => <tr
                key={i}
                style={{ cursor: 'pointer' }}
                onClick={() => this.setState({ [indexKey]: i, searchOpen: null })}
              >
                <td>{i + 1}</td>
                <td className="small">{p.model_id || [p.model_id_a, p.model_id_b].filter(Boolean).join(' / ')}</td>
                <td><BadgeColorHash id={p.cluster_id_a} /></td>
                <td><BadgeColorHash id={p.cluster_id_b} /></td>
              </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
  }

  renderTwinTab() {
    const pairs = this.visibleTwinPairs();
    const idx = Math.min(this.state.twinIndex, pairs.length - 1);
    const pair = pairs[idx];
    if (!pair) {
      return <div className="alert alert-success">No more twin pairs in queue.</div>;
    }

    const header = <>
      <strong>{pair.model_id}</strong>
      <SimilarityBadge value={pair.cosine_similarity} cosine digits={4} title="cluster-pair cosine similarity (symmetric median best match)" />
      <span className="text-muted">({idx + 1}/{pairs.length})</span>
      <span className="small">A <BadgeColorHash id={pair.cluster_id_a} /> vs B <BadgeColorHash id={pair.cluster_id_b} /></span>
      <ClusterPairAdminLink clusterIdA={pair.cluster_id_a} clusterIdB={pair.cluster_id_b} />
    </>;

    return <div>
      <div className="mt-1 mb-2 border rounded bg-light p-1 text-center">
        <button type="button" className="btn btn-success mr-2" onClick={this.handleTwinLink}>Same template (link)</button>
        <button type="button" className="btn btn-secondary mr-2" onClick={this.handleTwinReject}>Not duplicate</button>
        <button type="button" className="btn btn-info mr-2" title="Closely related but not the same: a minor variation, or one is a subset of the other. Resolves the pair without linking or rejecting it." onClick={this.handleTwinRelated}>Related (variant/subset)</button>
        <button type="button" className="btn btn-outline-primary" onClick={() => this.setState({ twinIndex: idx + 1 })}>Skip</button>
        {this.renderJump('twinIndex', pairs.length)}
        {this.renderSearch('twinIndex')}
      </div>
      <ClusterPairExplorer
        clusterIdA={pair.cluster_id_a}
        clusterIdB={pair.cluster_id_b}
        modelIdA={pair.model_id}
        modelIdB={pair.model_id}
        header={header}
      />
    </div>;
  }

  renderPhotoTab() {
    const photoCluster = this.state.photoClusters[this.state.photoIndex];
    if (!photoCluster) {
      const skipped = this.state.mixedClusters.length - this.state.photoClusters.length;
      return <div className="alert alert-success">
        No more mixed clusters in queue.
        {skipped > 0 ? <div className="small mt-2">{skipped} cluster(s) skipped because k-means did not produce two non-empty groups. Re-run analyze after updating the script.</div> : null}
      </div>;
    }

    const { summary, cluster, group0, group1 } = photoCluster;

    return <div>
      <div className="mt-1 mb-1 border rounded bg-light p-1">
        <button type="button" className="btn btn-outline-secondary mr-2" disabled={this.state.photoIndex <= 0} onClick={() => this.setState({ photoIndex: this.state.photoIndex - 1 })}>Previous</button>
        <button type="button" className="btn btn-outline-primary mr-2" onClick={() => this.setState({ photoIndex: this.state.photoIndex + 1 })}>Next cluster</button>
        {this.renderJump('photoIndex', this.state.photoClusters.length)}
        {!cluster ? null : <span className="text-muted small ml-2">Full cluster has {cluster.num_images_embedded} embedded thumbnails.</span>}
      </div>
      <div className="mb-2">
        <strong>{summary.model_id}</strong> — cluster <BadgeColorHash id={summary.cluster_id} />
        <span className="text-muted ml-2">separation {summary.separation_score}</span>
        <span className="text-muted ml-2">({this.state.photoIndex + 1}/{this.state.photoClusters.length})</span>
      </div>
      <div className="row">
        {this.renderPhotoGroupPanel(0, group0, summary.cluster_id, 'Group A')}
        {this.renderPhotoGroupPanel(1, group1, summary.cluster_id, 'Group B')}
      </div>
    </div>;
  }

  renderCrossModelTab() {
    const pairs = this.visibleCrossModelPairs();
    const idx = Math.min(this.state.crossIndex, pairs.length - 1);
    const pair = pairs[idx];
    if (!pair) {
      return <div className="alert alert-success">No more cross-model pairs in queue.</div>;
    }

    const header = <>
      <strong>{pair.model_id_a}</strong> <BadgeColorHash id={pair.cluster_id_a} />
      <SimilarityBadge value={pair.cosine_similarity} cosine digits={4} title="cross-model cosine similarity (symmetric median best match)" />
      <strong>{pair.model_id_b}</strong> <BadgeColorHash id={pair.cluster_id_b} />
      <span className="text-muted">({idx + 1}/{pairs.length})</span>
      <ClusterPairAdminLink clusterIdA={pair.cluster_id_a} clusterIdB={pair.cluster_id_b} />
    </>;

    return <div>
      <div className="mt-1 mb-2 border rounded bg-light p-1 text-center">
        <button type="button" className="btn btn-success mr-2" onClick={this.handleCrossModelLink}>Link as same global template</button>
        <button type="button" className="btn btn-secondary mr-2" onClick={this.handleCrossModelReject}>Not same (reject)</button>
        <button type="button" className="btn btn-info mr-2" title="Closely related but not the same: a minor variation, or one is a subset of the other. Resolves the pair without linking or rejecting it." onClick={this.handleCrossModelRelated}>Related (variant/subset)</button>
        <button type="button" className="btn btn-outline-primary" onClick={() => this.setState({ crossIndex: idx + 1 })}>Skip</button>
        {this.renderJump('crossIndex', pairs.length)}
        {this.renderSearch('crossIndex')}
      </div>
      <ClusterPairExplorer
        clusterIdA={pair.cluster_id_a}
        clusterIdB={pair.cluster_id_b}
        modelIdA={pair.model_id_a}
        modelIdB={pair.model_id_b}
        header={header}
      />
    </div>;
  }

  renderLinkedTab() {
    const pairs = this.visibleLinkedButFar();
    const idx = Math.min(this.state.linkedIndex, pairs.length - 1);
    const pair = pairs[idx];
    if (!pair) {
      return <div className="alert alert-success">No more linked-but-far pairs in queue.</div>;
    }

    const header = <>
      <strong>{pair.model_id_a}</strong> <BadgeColorHash id={pair.cluster_id_a} />
      <SimilarityBadge value={pair.cosine_similarity} cosine digits={4} title="linked-pair cosine similarity (low = current model disagrees with the link)" />
      <strong>{pair.model_id_b}</strong> <BadgeColorHash id={pair.cluster_id_b} />
      <span className="text-muted">({idx + 1}/{pairs.length})</span>
      <span className="small text-muted">linked via {(pair.link_sources || []).join(', ')}</span>
      <ClusterPairAdminLink clusterIdA={pair.cluster_id_a} clusterIdB={pair.cluster_id_b} />
    </>;

    return <div>
      <div className="mt-1 mb-2 border rounded bg-light p-1 text-center">
        <button type="button" className="btn btn-success mr-2" onClick={this.handleLinkedConfirm}>Confirm link (same)</button>
        <button type="button" className="btn btn-danger mr-2" onClick={this.handleLinkedReject}>Break link (different)</button>
        <button type="button" className="btn btn-info mr-2" title="Closely related but not the same: a minor variation, or one is a subset of the other. Resolves the pair without linking or rejecting it." onClick={this.handleLinkedRelated}>Related (variant/subset)</button>
        <button type="button" className="btn btn-outline-primary" onClick={() => this.setState({ linkedIndex: idx + 1 })}>Skip</button>
        {this.renderJump('linkedIndex', pairs.length)}
        {this.renderSearch('linkedIndex')}
      </div>
      <ClusterPairExplorer
        clusterIdA={pair.cluster_id_a}
        clusterIdB={pair.cluster_id_b}
        modelIdA={pair.model_id_a}
        modelIdB={pair.model_id_b}
        header={header}
      />
    </div>;
  }

  renderConflictsTab() {
    const { contradictions } = this.state;
    if (!contradictions.length) {
      return <div className="alert alert-success">No contradictions — every rejected pair is disconnected in the link graph.</div>;
    }
    return <div>
      <div className="alert alert-warning">
        These pairs were explicitly rejected but are still connected through other links (a chain
        over-merged them). Break more links in the linked-but-far queue, or undo the rejection by
        re-linking the pair in the twin queue.
      </div>
      <table className="table table-sm">
        <thead><tr><th>Cluster A</th><th>Cluster B</th><th>Direct edge sources</th></tr></thead>
        <tbody>
          {contradictions.map((row) => <tr key={`${row.cluster_id_a}|${row.cluster_id_b}`}>
            <td><BadgeColorHash id={row.cluster_id_a} /></td>
            <td><BadgeColorHash id={row.cluster_id_b} /></td>
            <td className="small text-muted">{(row.sources || []).join(', ') || '(indirect only)'}</td>
          </tr>)}
        </tbody>
      </table>
    </div>;
  }

  renderPageBody() {
    const {
      loading, error, tab, sampleName, samples, generatedAt, thresholds, stats, pendingOverrides, loadedOverrideCount, loadWarnings,
      photoClusters, contradictions,
    } = this.state;
    // Live queue counts: shrink as decisions (saved or pending) answer pairs, directly or by propagation.
    const twinPairs = this.visibleTwinPairs();
    const crossModelPairs = this.visibleCrossModelPairs();
    const linkedButFar = this.visibleLinkedButFar();

    return <div className="test-embedding-page margin p-3">
      <h4>Fusebox dataset review</h4>
      <div className="text-muted small mb-3">
        sample
        <select
          className="form-control form-control-sm d-inline-block mx-2 align-middle"
          style={{ width: 'auto' }}
          value={sampleName}
          onChange={(e) => this.loadData(e.target.value)}
        >
          {!samples.some((s) => s.name === sampleName) ? <option value={sampleName}>{sampleName || '(none)'}</option> : null}
          {samples.map((s) => <option key={s.name} value={s.name} disabled={!s.hasModeration}>{s.name}{s.hasModeration ? '' : ' (no moderation.json)'}</option>)}
        </select>
        {generatedAt ? <> built <strong>{generatedAt.slice(0, 16).replace('T', ' ')}</strong></> : null}
        {stats?.clustersAnalyzed != null ? <> · <strong>{stats.clustersAnalyzed}</strong> clusters / {stats.imagesEmbedded} embedded images</> : null}
        {thresholds?.minPairSimilarity != null ? <> · pair ≥ {thresholds.minPairSimilarity} · linked-far ≤ {thresholds.linkedFarMaxSimilarity}</> : null}
      </div>

      {error ? <div className="alert alert-danger">{error}<br /><button type="button" className="btn btn-sm btn-outline-danger mt-2" onClick={() => this.loadData()}>Retry</button></div> : null}
      {loading ? <div className="alert alert-info">Loading moderation payload…</div> : null}

      {loadWarnings?.length ? <div className="alert alert-warning">
        Some files failed to load:
        <ul className="mb-0 mt-2">{loadWarnings.map((msg) => <li key={msg}>{msg}</li>)}</ul>
      </div> : null}

      <div className="mb-3">
        <button type="button" className={`btn btn-sm mr-2 ${tab === 'twins' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => this.setState({ tab: 'twins' })}>Twin clusters ({twinPairs.length})</button>
        <button type="button" className={`btn btn-sm mr-2 ${tab === 'photos' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => this.setState({ tab: 'photos' })}>Photo types ({photoClusters.length})</button>
        <button type="button" className={`btn btn-sm mr-2 ${tab === 'cross' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => this.setState({ tab: 'cross' })}>Cross-model ({crossModelPairs.length})</button>
        <button type="button" className={`btn btn-sm mr-2 ${tab === 'linked' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => this.setState({ tab: 'linked' })}>Linked-but-far ({linkedButFar.length})</button>
        <button type="button" className={`btn btn-sm mr-2 ${tab === 'conflicts' ? 'btn-primary' : 'btn-outline-primary'} ${contradictions.length ? 'text-danger' : ''}`} onClick={() => this.setState({ tab: 'conflicts' })}>Conflicts ({contradictions.length})</button>
        <span className="ml-3 small text-muted">
          Pending: {pendingOverrides.length} | Loaded from file: {loadedOverrideCount}
        </span>
        <button type="button" className="btn btn-sm btn-warning ml-2" disabled={!pendingOverrides.length} onClick={() => this.flushOverrides()}>Save to server</button>
        <button type="button" className="btn btn-sm btn-outline-secondary ml-1" disabled={!pendingOverrides.length} onClick={() => this.downloadPendingOverrides()}>Download JSONL</button>
        <button type="button" className="btn btn-sm btn-outline-info ml-1" onClick={() => this.loadData()}>Reload data</button>
      </div>

      {!loading && !error ? <>
        {tab === 'twins' ? this.renderTwinTab() : null}
        {tab === 'photos' ? this.renderPhotoTab() : null}
        {tab === 'cross' ? this.renderCrossModelTab() : null}
        {tab === 'linked' ? this.renderLinkedTab() : null}
        {tab === 'conflicts' ? this.renderConflictsTab() : null}
      </> : null}
      {this.renderSearchModal()}
    </div>;
  }
}
